import React from 'react';
import { Box, IconButton as MuiIconButton, Badge } from '@mui/material';
// Spatial-analysis rail button (from develop) still uses the MUI IconButton API
// (sx + children + the throbbing TravelExplore icon). The rest of the rail uses
// the liftango IconButton imported below; alias MUI's to avoid the name clash.
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@liftango/ui-elements';
import { MAP_UI_TOP_BAR_HEIGHT_PX, MAP_UI_RIGHT_RAIL_WIDTH_PX } from '../mapUiLayout';
import { opsColors } from '../../../theme/opsTokens';
import BasemapSwitcher from '../BasemapSwitcher';

import { useSimulation } from '../../../context/SimulationContext';
import { useSpatialAnalysis } from '../../../context/SpatialAnalysisContext';
import { LIFTANGO_ORANGE, STEP_ICON, throb } from './spatialAnalysisIcons';
import Tooltip from '@mui/material/Tooltip';
import { trackAnalyticsEvent } from '../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../services/eventTaxonomy';
import type { MapPanelName } from '../../../services/eventTypes';
import { useFeatureFlags } from '../../../services/featureFlags/useFeatureFlags';
import { isPanelRuntimeEnabled } from '../../../services/featureFlags/runtimeFeatureSets';
import { diagnosticsCollector } from '../../../services/diagnostics/diagnosticsCollector';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
interface MapSidebarProps {
  activeRightDrawer:
    | null
    | 'fleetMenu'
    | 'simulationResults'
    | 'generateRides'
    | 'fixedRoutes'
    | 'dataTransfer'
    | 'attributeTable'
    | 'populationAnalysis'
    | 'spatialAnalysis'
    | 'bugReport';
  onToggleRightDrawer: (key: NonNullable<MapSidebarProps['activeRightDrawer']>) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  wizardActive?: boolean;
  /**
   * True while the post-complete GTFS feed import is in flight (owned by map.tsx).
   * Keeps the spatial-analysis toggle in a loading treatment after the run reports
   * `complete` but transit routes are still being fetched into Fixed Routes.
   */
  gtfsImporting?: boolean;
}

// Map sidebar buttons - no longer includes logo since it's in the main navigation
const MapSidebarButtons = () => {
  return null; // Remove the logo pill since navigation is now in the top bar
};

const MapSidebar: React.FC<MapSidebarProps> = ({
  activeRightDrawer,
  onToggleRightDrawer,
  onZoomIn,
  onZoomOut,
  wizardActive = false,
  gtfsImporting = false,
}) => {
  const { t } = useTranslation('map');
  const { status, runQueue } = useSimulation();
  const { status: spatialStatus, activeStepId: spatialActiveStepId } = useSpatialAnalysis();
  const { isFeatureRuntimeEnabled, flags } = useFeatureFlags();

  const isPanelEnabled = (key: NonNullable<MapSidebarProps['activeRightDrawer']>): boolean =>
    isPanelRuntimeEnabled(key, isFeatureRuntimeEnabled);

  const handleToggle = (
    key: NonNullable<MapSidebarProps['activeRightDrawer']>,
    panelName?: MapPanelName
  ) => {
    if (!isPanelEnabled(key)) {
      return;
    }
    if (panelName) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.panel_toggled, {
        panel_name: panelName,
        is_open: activeRightDrawer !== key,
        screen_area: 'map_sidebar',
        timestamp: new Date().toISOString(),
      });
    }
    diagnosticsCollector.addBreadcrumb('map_sidebar', 'panel_toggled', {
      panel_key: key,
      panel_name: panelName ?? key,
      is_opening: activeRightDrawer !== key,
      wizard_active: wizardActive,
    });
    onToggleRightDrawer(key);
  };

  const simTooltip =
    status === 'running' && runQueue.length > 0
      ? t('sidebar.simulationQueueRunning', {
          current: runQueue.filter(r => r.status !== 'queued' && r.status !== 'cancelled').length,
          total: runQueue.length,
        }) :
    status === 'running'  ? t('sidebar.simulationRunning')  :
    status === 'finished' ? t('sidebar.simulationFinished') :
    status === 'failed'   ? t('sidebar.simulationFailed')   :
    t('sidebar.openSimulationResults');
  const iconSx = { color: 'text.primary', fontSize: 30 } as const;
  // Drawer Papers sit at zIndex 2201; ensure tooltips render above them
  const tooltipSlotProps = { popper: { sx: { zIndex: 2600 } } } as const;
  const slotSx = (isActive: boolean): React.CSSProperties => ({
    position: 'relative',
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    borderRadius: 10,
    color: 'inherit',
    backgroundColor: isActive ? opsColors.neutral.panelAlt : 'transparent',
    border: `1px solid ${isActive ? opsColors.neutral.lineStrong : 'transparent'}`,
    transition: 'background-color 0.16s ease',
    boxShadow: 'none',
  });

  const isDrawerButtonLocked = (key: NonNullable<MapSidebarProps['activeRightDrawer']>): boolean =>
    wizardActive && key !== 'simulationResults';

  const lockedButtonSx = {
    filter: 'blur(1.8px)',
    opacity: 0.48,
  } as const;

  // While a spatial analysis run is active, the rail toggle mirrors the in-drawer
  // progress treatment: the current phase icon, throbbing in Liftango orange.
  const spatialRunActive =
    spatialStatus === 'queued' || spatialStatus === 'running' || spatialStatus === 'drafting';
  // After the run reports `complete`, the GTFS feed is still being imported into
  // Fixed Routes (transit routes "appear later"). Keep a loading treatment going
  // so the rail signals work is ongoing — the transit phase icon throbbing.
  const spatialImporting = spatialStatus === 'complete' && gtfsImporting;
  const spatialLoading = spatialRunActive || spatialImporting;
  const spatialIcon = spatialImporting
    ? STEP_ICON.transit
    : spatialRunActive && spatialActiveStepId && STEP_ICON[spatialActiveStepId]
      ? STEP_ICON[spatialActiveStepId]
      : null;

  const showFixedRoutesButton = isPanelEnabled('fixedRoutes');
  const showDataTransferButton = isPanelEnabled('dataTransfer');
  // Fail-open: show unless an admin explicitly disabled the flag for this customer.
  const showSpatialAnalysisButton = flags['spatial_analysis']?.enabled !== false;

  return (
    <>
      <MapSidebarButtons />
      <Box sx={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        right: 0,
        zIndex: 2100,
        width: MAP_UI_RIGHT_RAIL_WIDTH_PX,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        paddingTop: `calc(${MAP_UI_TOP_BAR_HEIGHT_PX}px + 14px)`,
        paddingBottom: 1,
        overflowY: 'auto',
        overflowX: 'visible',
        transition: 'right 0.3s',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        backgroundColor: opsColors.neutral.panel,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 14,
        borderLeft: `1px solid ${opsColors.neutral.line}`,
        borderTop: 'none',
        borderBottom: `1px solid ${opsColors.neutral.line}`,
        boxShadow: 'none',
      }}
      data-testid="map.sidebar.root"
      data-drawer-state={activeRightDrawer ?? 'none'}
      >
        <Tooltip title={t('sidebar.openPopulationAnalysis')} placement="left" slotProps={tooltipSlotProps}>
          <IconButton
            variant="ghost"
            label="Open Population Analysis Drawer"
            icon={<Icon size={20} name="users" style={iconSx} />}
            onClick={() => handleToggle('populationAnalysis', 'population_analysis')}
            disabled={!isPanelEnabled('populationAnalysis') || isDrawerButtonLocked('populationAnalysis')}
            style={{
              ...slotSx(activeRightDrawer === 'populationAnalysis'),
              ...(isDrawerButtonLocked('populationAnalysis') ? lockedButtonSx : {}),
            }}
            data-testid="map.sidebar.population-analysis"
          />
        </Tooltip>
        {showSpatialAnalysisButton && (
          <Tooltip title={t('sidebar.openSpatialAnalysis')} placement="left" slotProps={tooltipSlotProps}>
            <MuiIconButton
              onClick={() => handleToggle('spatialAnalysis')}
              disabled={isDrawerButtonLocked('spatialAnalysis')}
              sx={{
                ...slotSx(activeRightDrawer === 'spatialAnalysis'),
                ...(isDrawerButtonLocked('spatialAnalysis') ? lockedButtonSx : {}),
              }}
              aria-label={t('sidebar.openSpatialAnalysis')}
              data-testid="map.sidebar.spatial-analysis"
              data-run-active={spatialRunActive ? 'true' : 'false'}
              data-gtfs-importing={spatialImporting ? 'true' : 'false'}
            >
              {spatialLoading ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    color: LIFTANGO_ORANGE,
                    animation: `${throb} 1.2s ease-in-out infinite`,
                    // Scale the small (20px) phase/base icons up to match the rail's 30px icons.
                    '& .MuiSvgIcon-root': { fontSize: 30 },
                  }}
                >
                  {spatialIcon ?? <TravelExploreIcon />}
                </Box>
              ) : (
                <TravelExploreIcon sx={iconSx} />
              )}
            </MuiIconButton>
          </Tooltip>
        )}
        <Tooltip title={t('sidebar.openGenerateRides')} placement="left" slotProps={tooltipSlotProps}>
          <IconButton
            variant="ghost"
            label="Open Generate Rides Drawer"
            icon={<Icon size={20} name="rocket" style={iconSx} />}
            onClick={() => handleToggle('generateRides', 'generate_rides')}
            disabled={!isPanelEnabled('generateRides') || isDrawerButtonLocked('generateRides')}
            style={{
              ...slotSx(activeRightDrawer === 'generateRides'),
              ...(isDrawerButtonLocked('generateRides') ? lockedButtonSx : {}),
            }}
            data-testid="map.sidebar.generate-rides"
          />
        </Tooltip>
        {showFixedRoutesButton && (
          <Tooltip title={t('sidebar.openFixedRoutes')} placement="left" slotProps={tooltipSlotProps}>
            <IconButton
              variant="ghost"
              label="Open Fixed Routes Drawer"
              icon={<Icon size={20} d={ICON_FALLBACKS.AltRoute} style={iconSx} />}
              onClick={() => handleToggle('fixedRoutes', 'fixed_routes')}
              disabled={isDrawerButtonLocked('fixedRoutes')}
              style={{
                ...slotSx(activeRightDrawer === 'fixedRoutes'),
                ...(isDrawerButtonLocked('fixedRoutes') ? lockedButtonSx : {}),
              }}
              data-testid="map.sidebar.fixed-routes"
            />
          </Tooltip>
        )}
        <Tooltip title={t('sidebar.openFleetMenu')} placement="left" slotProps={tooltipSlotProps}>
          <IconButton
            variant="ghost"
            label="Open Fleet Menu"
            icon={<Icon size={20} name="bus" style={iconSx} />}
            onClick={() => handleToggle('fleetMenu', 'fleet_menu')}
            disabled={isDrawerButtonLocked('fleetMenu')}
            style={{
              ...slotSx(activeRightDrawer === 'fleetMenu'),
              ...(isDrawerButtonLocked('fleetMenu') ? lockedButtonSx : {}),
            }}
            data-testid="map.sidebar.fleet-menu"
          />
        </Tooltip>
        <Tooltip title={t('sidebar.openAttributeTable')} placement="left" slotProps={tooltipSlotProps}>
          <IconButton
            variant="ghost"
            label="Open Attribute Table"
            icon={<Icon size={20} name="table" style={iconSx} />}
            onClick={() => handleToggle('attributeTable', 'attribute_table')}
            disabled={!isPanelEnabled('attributeTable') || isDrawerButtonLocked('attributeTable')}
            style={{
              ...slotSx(activeRightDrawer === 'attributeTable'),
              ...(isDrawerButtonLocked('attributeTable') ? lockedButtonSx : {}),
            }}
            data-testid="map.sidebar.attribute-table"
          />
        </Tooltip>
        <Tooltip title={t('sidebar.simulationStatusColors', { tooltip: simTooltip })} placement="left" slotProps={tooltipSlotProps}>
          <IconButton
            variant="ghost"
            label={simTooltip}
            icon={(
              <Badge
                variant="dot"
                invisible={status === 'idle'}
                sx={{
                  '& .MuiBadge-dot': {
                    width: 10,
                    height: 10,
                    backgroundColor:
                      status === 'running'  ? 'warning.main' :
                      status === 'finished' ? 'success.main' :
                      status === 'failed'   ? 'error.main'   : 'transparent',
                    border: '1.5px solid rgba(0,0,0,0.18)',
                    animation: status === 'running' ? 'simDotPulse 1.4s ease-in-out infinite' : 'none',
                  },
                }}
              >
                <Icon size={20} d={ICON_FALLBACKS.Timeline} style={iconSx} />
              </Badge>
            )}
            onClick={() => handleToggle('simulationResults', 'simulation_results')}
            disabled={!isPanelEnabled('simulationResults')}
            style={slotSx(activeRightDrawer === 'simulationResults')}
            data-testid="map.sidebar.simulation-results"
          />
        </Tooltip>
        {showDataTransferButton && (
          <Tooltip title={t('sidebar.openDataTransfer')} placement="left" slotProps={tooltipSlotProps}>
            <IconButton
              variant="ghost"
              label="Open Data Transfer Drawer"
              icon={<Icon size={20} d={ICON_FALLBACKS.ImportExport} style={iconSx} />}
              onClick={() => handleToggle('dataTransfer', 'data_transfer')}
              disabled={isDrawerButtonLocked('dataTransfer')}
              style={{
                ...slotSx(activeRightDrawer === 'dataTransfer'),
                ...(isDrawerButtonLocked('dataTransfer') ? lockedButtonSx : {}),
              }}
              data-testid="map.sidebar.data-transfer"
            />
          </Tooltip>
        )}
        <Tooltip title={t('sidebar.openBugReport')} placement="left" slotProps={tooltipSlotProps}>
          <IconButton
            variant="ghost"
            label="Open Bug Report Drawer"
            icon={<Icon size={20} d={ICON_FALLBACKS.BugReport} style={iconSx} />}
            onClick={() => handleToggle('bugReport')}
            disabled={isDrawerButtonLocked('bugReport')}
            style={{
              ...slotSx(activeRightDrawer === 'bugReport'),
              ...(isDrawerButtonLocked('bugReport') ? lockedButtonSx : {}),
            }}
            data-testid="map.sidebar.bug-report"
          />
        </Tooltip>
        <Box sx={{
          mt: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.9,
          px: 0.75,
          pt: 1.25,
          pb: 1.25,
          borderTop: `1px solid ${opsColors.neutral.line}`,
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            width: '100%',
          }}>
            <IconButton
              variant="ghost"
              label="Zoom in"
              icon={<Icon size={18} name="plus" style={iconSx} />}
              onClick={onZoomIn}
              style={slotSx(false)}
            />
            <IconButton
              variant="ghost"
              label="Zoom out"
              icon={<Icon size={18} name="minus" style={iconSx} />}
              onClick={onZoomOut}
              style={slotSx(false)}
            />
          </Box>
          <BasemapSwitcher embedded />
        </Box>
      </Box>
    </>
  );
};

export default MapSidebar;
